import { useEffect, useRef } from 'react';
import L, { type LayerGroup } from 'leaflet';
import { useMap } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { loadMilSym, type MilSym } from './tactical/renderer';
import { createSymbol } from './tactical/registry';
import { AttackLine } from './tactical/AttackLine';
import type { TacticalGraphic } from '../../store/graphicsSlice';

interface GraphicsLayerProps {
    /** Raised when the user clicks a rendered graphic. */
    onSelectGraphic?: (graphicId: string) => void;
    /**
     * Graphic to leave unrendered — the one being redrawn. Its replacement is
     * live in the draw tool's preview, and showing both at once would leave the
     * user unable to tell which shape is which.
     */
    hiddenGraphicId?: string | null;
}

/**
 * Renders every persisted tactical graphic from Redux.
 *
 * This layer — not the drawing tool — owns finished graphics. That inversion is
 * what makes them behave like real entities: they survive tool switches and map
 * remounts, reload with the page, disappear when a delete tombstone arrives, and
 * a graphic drawn on another device takes exactly the same path to the screen as
 * one drawn locally. (The tool previously kept them in a private array that was
 * wiped on unmount, so drawn arrows silently vanished.)
 *
 * Graphics are re-rendered from scratch whenever the collection changes. That is
 * cheap because the set is small and hand-drawn, and it keeps the layer free of
 * diffing logic that could drift from the store.
 */
export default function GraphicsLayer({
    onSelectGraphic,
    hiddenGraphicId = null,
}: GraphicsLayerProps) {
    const map = useMap();
    const graphics = useSelector((state: RootState) => state.graphics.value);
    const groupRef = useRef<LayerGroup | null>(null);
    const milSymRef = useRef<MilSym | null>(null);
    const disposedRef = useRef(false);

    useEffect(() => {
        const group = L.layerGroup().addTo(map);
        groupRef.current = group;
        disposedRef.current = false;
        return () => {
            disposedRef.current = true;
            group.remove();
            groupRef.current = null;
        };
    }, [map]);

    useEffect(() => {
        const group = groupRef.current;
        if (!group) return;

        let cancelled = false;

        const render = (milSym: MilSym) => {
            if (cancelled || disposedRef.current) return;
            group.clearLayers();
            for (const graphic of graphics) {
                if (graphic.id === hiddenGraphicId) continue;
                const layer = buildLayer(graphic, map, milSym);
                if (!layer) continue;
                if (onSelectGraphic) {
                    // Leaflet paths bubble clicks to the map by default, which
                    // would also drop a vertex while the draw tool is active.
                    layer.on('click', (event) => {
                        L.DomEvent.stopPropagation(event);
                        onSelectGraphic(graphic.id);
                    });
                }
                layer.addTo(group);
            }
        };

        if (milSymRef.current) {
            render(milSymRef.current);
        } else {
            // The multipoint renderer is a large lazy chunk; it is only fetched
            // once a graphic actually needs drawing.
            void loadMilSym().then((milSym) => {
                if (cancelled || disposedRef.current) return;
                milSymRef.current = milSym;
                render(milSym);
            });
        }

        return () => {
            cancelled = true;
        };
    }, [graphics, map, onSelectGraphic, hiddenGraphicId]);

    return null;
}

/**
 * Build the Leaflet layer for one stored graphic.
 *
 * Returns null for a SIDC this client doesn't know how to draw — a graphic from
 * a newer client must be skipped, not crash the map.
 */
function buildLayer(
    graphic: TacticalGraphic,
    map: L.Map,
    milSym: MilSym,
): L.Layer | null {
    const symbol = createSymbol(graphic.sidc, map, milSym);
    if (!symbol) return null;

    if (symbol instanceof AttackLine) {
        const { width_m, head_length_m } = graphic.params ?? {};
        // Absent params (an older row) fall back to the pixel defaults, so
        // pre-existing graphics keep rendering.
        symbol.setParams(
            width_m != null && head_length_m != null
                ? { width_m, head_length_m }
                : null,
        );
    }

    const latlngs = graphic.points.map(([lng, lat]) => L.latLng(lat, lng));
    symbol.setPoints(latlngs);
    if (!symbol.isDrawable) return null;
    symbol.finalize();
    addHitArea(symbol.layer, latlngs);
    return symbol.layer;
}

/**
 * Stroke width (px) of the invisible pick target laid along a graphic's
 * centreline. Generous on purpose: it is what the user aims at.
 */
const HIT_WEIGHT = 24;

/**
 * Give a finished graphic something clickable along its whole length.
 *
 * The rendered geometry alone is a poor target: an arrow body is a *fill-less*
 * polygon (see `tacticalFeatureStyle`), so the browser only hit-tests its
 * 3px outline — clicking the middle of a fat arrow hits nothing at all. This
 * adds a transparent, thick polyline along the centreline the user actually drew,
 * so clicking anywhere on the graphic selects it.
 *
 * Only persisted graphics get this. The drawing preview must stay
 * non-interactive, or the hit area would swallow the map clicks that place the
 * next vertex.
 */
function addHitArea(group: L.FeatureGroup, latlngs: L.LatLng[]): void {
    L.polyline(latlngs, {
        // `opacity: 0` keeps it invisible while still painted for hit-testing.
        opacity: 0,
        weight: HIT_WEIGHT,
        interactive: true,
        bubblingMouseEvents: false,
    }).addTo(group);
}
